import React from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';

import { Route as ImplementationsRoute } from './_index';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { Route as ImplementationDetailsRoute } from '~/core/routes/settings/implementations/[implementationId]/_index';
import { Route as FacilityDetailsRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/_index';
import { Route as CommitmentDetailRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/_index';
import { Route as CommitmentsRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/_index';
import { Route as ExpensesDetailRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/_index';
import { Route as ExpensesRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/_index';
import { Route as FacilityMechanicsDetailsRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/facility-mechanics/_index';
import { Route as PartiesRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/_index';
import { Route as PricingOptionsDetailsRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/[pricingOptionId]/_index';
import { Route as PricingOptionsRoute } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/_index';
import { ConfirmDeleteModalProvider } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { ConfirmDuplicateModalProvider } from '~/core/routes/settings/implementations/common/ConfirmDuplicateModalContext';
import { Route as CreateImplementationRoute } from '~/core/routes/settings/implementations/create/_index';
import { ImplementationsLayout } from '~/core/routes/settings/implementations/layout/ImplementationsLayout';

export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';

  // @implementation - (https://github.com/finleytechnologies/atlas/pull/16585#discussion_r2082266335)
  // document upload functionality exists within the IP route.
  // we need to be able to access document upload, so we enable the IP route if either flag is enabled
  // within the IP UI, we'll selectively disable certain parts of the UI depending on the values of these two flags

  if (
    context.flags.productsImplementationPortalDocumentUploadPermissions ===
    'allow'
  )
    return 'allow';

  return context.flags.productsImplementationPortalPermissions;
};

// Exports
export const Router: RouteObject = {
  element: (
    <PermissionGuard
      permissions={permissions}
      fallback={<Navigate to="/" replace />}
    >
      <RouteErrorBoundary
        fallback={({ message, showErrorToast }) => {
          showErrorToast(message);
          return <Navigate to="/settings" />;
        }}
      >
        <ConfirmDeleteModalProvider>
          <ConfirmDuplicateModalProvider>
            <Outlet />
          </ConfirmDuplicateModalProvider>
        </ConfirmDeleteModalProvider>
      </RouteErrorBoundary>
    </PermissionGuard>
  ),
  // Relative to /implementations/*
  children: [
    { index: true, element: <ImplementationsRoute /> },
    { path: 'create', element: <CreateImplementationRoute /> },
    { path: ':implementationId', element: <ImplementationDetailsRoute /> },

    // Group all routes with :facilityId under a common parent
    {
      path: ':implementationId/facilities/:facilityId',
      element: <ImplementationsLayout />,
      children: [
        { index: true, element: <FacilityDetailsRoute /> },
        {
          path: 'facility-mechanics',
          element: <FacilityMechanicsDetailsRoute />,
        },
        {
          path: 'expense-mechanics',
          element: <ExpensesRoute />,
        },
        {
          path: 'expense-mechanics/unusedFee/:counterpartySlug', // todo: @markhaghani this is temporary. reconsider liability account's unique identifier when adding other fees
          element: <ExpensesDetailRoute />,
        },
        {
          path: 'parties',
          element: <PartiesRoute />,
        },
        {
          path: 'commitments',
          element: <CommitmentsRoute />,
        },
        {
          path: 'commitments/:commitmentId',
          element: <CommitmentDetailRoute />,
        },
        {
          path: 'pricing-options',
          element: <PricingOptionsRoute />,
        },
        {
          path: 'pricing-options/:pricingOptionId',
          element: <PricingOptionsDetailsRoute />,
        },
      ],
    },

    { path: '*', element: <Navigate to="./" replace /> },
  ],
};
